'use client'

import { AnimatePresence, m } from 'framer-motion'
import { ArrowUpRight, Linkedin } from 'lucide-react'
import { useState } from 'react'

const team = [
  {
    id: 1,
    name: 'Олег Носиков',
    role: 'Основатель и руководитель',
    initials: 'ОН',
    gradient: 'from-blue-500/30 to-cyan-500/10',
    quote:
      'Мы создаём не просто сайты и рекламу — мы строим системы, которые приносят деньги. Каждый проект для нас — это вызов и возможность доказать, что digital работает.',
  },
  {
    id: 2,
    name: 'Аскер Симбулетов',
    role: 'Разработчик',
    initials: 'АС',
    gradient: 'from-purple-500/30 to-indigo-500/10',
    quote:
      'Пишет код, который работает быстро и стабильно. Фронтенд, бэкенд, интеграции — превращает дизайн в живой продукт.',
  },
  {
    id: 3,
    name: 'Юлия Носикова',
    role: 'SMM-специалист',
    initials: 'ЮН',
    gradient: 'from-pink-500/30 to-rose-500/10',
    quote:
      'Строит присутствие брендов в соцсетях. Стратегия, контент, комьюнити — превращает подписчиков в лояльных клиентов.',
  },
  {
    id: 4,
    name: 'Ксения Эндовицкая',
    role: 'Дизайнер',
    initials: 'КЭ',
    gradient: 'from-orange-500/30 to-amber-500/10',
    quote:
      'Создаёт визуал, который продаёт. UI/UX, фирменный стиль, креативы — каждый пиксель работает на результат.',
  },
]

export default function AgencyTeam() {
  const [activeId, setActiveId] = useState<number | null>(1)

  return (
    <section className="relative overflow-hidden py-16 sm:py-24">
      <div className="home-container relative z-10">
        <m.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 className="home-title mb-6">
            Команда <span className="text-gray-500">лидеров</span>
          </h2>
        </m.div>

        <div className="space-y-4 lg:hidden">
          {team.map((member, index) => {
            const isActive = activeId === member.id || (!activeId && index === 0)
            return (
              <m.div
                key={member.id}
                layout
                transition={{ type: 'spring', stiffness: 220, damping: 26 }}
                className="overflow-hidden rounded-3xl border border-white/10 bg-white/5 backdrop-blur-md"
              >
                <button
                  type="button"
                  onClick={() => setActiveId(member.id)}
                  className="w-full text-left"
                >
                  <div
                    className={[
                      'relative w-full overflow-hidden',
                      isActive ? 'h-[440px] sm:h-[520px]' : 'h-[120px] sm:h-[140px]',
                    ].join(' ')}
                  >
                    <div
                      className={`absolute inset-0 bg-gradient-to-br ${member.gradient} bg-[#0c1220]`}
                    />
                    <span
                      className={[
                        'absolute inset-0 flex items-center justify-center font-black text-white/10 transition-transform duration-700',
                        isActive ? 'scale-100 text-[7rem]' : 'scale-[1.03] text-[3rem]',
                      ].join(' ')}
                    >
                      {member.initials}
                    </span>

                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-transparent" />

                    <div className="absolute right-0 bottom-0 left-0 p-5 sm:p-6">
                      <div className="flex items-end justify-between gap-4">
                        <div className="min-w-0">
                          <h3 className="home-card-title-sm truncate text-white">{member.name}</h3>
                          <p className="home-subnote text-gray-300/70">{member.role}</p>
                        </div>

                        <div className="flex shrink-0 gap-2">
                          <span className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/10 backdrop-blur">
                            <ArrowUpRight
                              className={[
                                'h-5 w-5 text-white transition-transform duration-300',
                                isActive ? 'rotate-45' : 'rotate-0',
                              ].join(' ')}
                            />
                          </span>
                        </div>
                      </div>

                      <AnimatePresence initial={false} mode="popLayout">
                        {isActive ? (
                          <m.div
                            key="details"
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 8 }}
                            transition={{ duration: 0.25 }}
                            className="mt-5 sm:mt-6"
                          >
                            <p className="home-subnote border-l-2 border-blue-500 pl-4 leading-relaxed text-gray-200/90">
                              «{member.quote}»
                            </p>

                            <div className="mt-5 flex gap-3">
                              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/10 backdrop-blur transition-colors hover:bg-white/15">
                                <Linkedin className="h-5 w-5 text-white" />
                              </span>
                              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/10 backdrop-blur transition-colors hover:bg-white/15">
                                <ArrowUpRight className="h-5 w-5 text-white" />
                              </span>
                            </div>
                          </m.div>
                        ) : null}
                      </AnimatePresence>
                    </div>
                  </div>
                </button>
              </m.div>
            )
          })}
        </div>

        <div className="hidden h-[520px] gap-3 lg:flex xl:h-[600px] xl:gap-4">
          {team.map((member) => (
            <m.div
              key={member.id}
              layout
              onClick={() => setActiveId(member.id)}
              onHoverStart={() => setActiveId(member.id)}
              className={`relative cursor-pointer overflow-hidden rounded-3xl transition-all duration-500 ease-[bezier(0.25,1,0.5,1)] ${activeId === member.id ? 'flex-[3]' : 'flex-[1]'}`}
            >
              <div
                className={`absolute inset-0 bg-gradient-to-br ${member.gradient} bg-[#0c1220]`}
              />
              <span
                className={`absolute inset-0 flex items-center justify-center font-black text-white/10 transition-all duration-700 ${activeId === member.id ? 'text-[9rem] opacity-100' : 'text-[4rem] opacity-50'}`}
              >
                {member.initials}
              </span>

              <div
                className={`absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent transition-opacity duration-500 ${activeId === member.id ? 'opacity-80' : 'opacity-60'}`}
              />

              <div className="absolute inset-0 flex flex-col justify-end p-8">
                <div
                  className={`absolute bottom-8 left-8 transition-opacity duration-300 ${activeId === member.id ? 'pointer-events-none opacity-0' : 'opacity-100'}`}
                >
                  <h3 className="rotate-180 text-xl font-bold tracking-widest text-white [writing-mode:vertical-rl] xl:text-2xl">
                    {member.name}
                  </h3>
                </div>

                <div
                  className={`transform transition-all delay-100 duration-500 ${activeId === member.id ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
                >
                  <div className="mb-4 flex items-center justify-between">
                    <div>
                      <h3 className="home-card-title mb-2 text-white">{member.name}</h3>
                      <p className="home-meta text-blue-300">{member.role}</p>
                    </div>
                    <div className="flex gap-3">
                      <button
                        className="group rounded-full border border-white/10 bg-white/10 p-3 backdrop-blur-md transition-colors hover:bg-white/20"
                        aria-label={`LinkedIn: ${member.name}`}
                      >
                        <Linkedin className="h-5 w-5 text-white transition-transform group-hover:scale-110" />
                      </button>
                      <button
                        className="group rounded-full border border-white/10 bg-white/10 p-3 backdrop-blur-md transition-colors hover:bg-white/20"
                        aria-label={`Подробнее: ${member.name}`}
                      >
                        <ArrowUpRight className="h-5 w-5 text-white transition-transform group-hover:rotate-45" />
                      </button>
                    </div>
                  </div>
                  <p className="home-subnote max-w-lg border-l-2 border-blue-500 pl-4 text-gray-300">
                    «{member.quote}»
                  </p>
                </div>
              </div>
            </m.div>
          ))}
        </div>
      </div>
    </section>
  )
}
